/* Native scrolling keeps the first product image usable before JS initialization.
   It changes only compatible, explicitly marked main product galleries. */
(() => {
  class RhamaNativeGallery {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('.swiper-main > .swiper-wrapper');
      this.slides = Array.from(this.track.children);
      this.thumbs = Array.from(root.querySelectorAll('.swiper-controls__thumbnail'));
      this.index = 0;
      this.navigation = null;
      this.track.tabIndex = 0;
      this.track.setAttribute('aria-label', 'Product images');
      root.querySelectorAll('.swiper-button').forEach(button => {
        button.setAttribute('role', 'button');
        button.tabIndex = 0;
        button.setAttribute('aria-label', button.classList.contains('swiper-button-next') ? 'Next image' : 'Previous image');
      });
      this.abort = new AbortController();
      const opts = {signal: this.abort.signal};
      // Keep a completed mouse drag from activating the thumbnail under it.
      root.addEventListener('pointerdown', () => { this.suppressClick = false; }, {...opts, capture:true});
      root.addEventListener('click', e => {
        if (!this.suppressClick || e.detail === 0) return;
        this.suppressClick = false;
        e.preventDefault(); e.stopImmediatePropagation();
      }, {...opts, capture:true});
      root.addEventListener('click', e => {
        const thumb = e.target.closest('.swiper-controls__thumbnail');
        const next = e.target.closest('.swiper-button-next');
        const prev = e.target.closest('.swiper-button-prev');
        if (!thumb && !next && !prev) return;
        e.preventDefault();
        this.go(thumb ? Number(thumb.dataset.index) : this.index + (next ? 1 : -1));
      }, opts);
      root.addEventListener('keydown', e => {
        const arrow = e.target.closest('.swiper-button');
        if (arrow && ['Enter', ' '].includes(e.key)) {
          e.preventDefault();
          this.go(this.index + (arrow.classList.contains('swiper-button-next') ? 1 : -1));
          return;
        }
        if (!['ArrowLeft','ArrowRight','Home','End'].includes(e.key)) return;
        e.preventDefault();
        this.go(e.key === 'Home' ? 0 : e.key === 'End' ? this.slides.length - 1 : this.index + (e.key === 'ArrowRight' ? 1 : -1));
      }, opts);
      const interruptNavigation = () => {
        if (this.navigation !== null) this.track.scrollTo({left:this.track.scrollLeft, behavior:'instant'});
        this.navigation = null;
        clearTimeout(this.scrollEnd);
        const index = this.nearestIndex();
        if (index !== this.index) { this.mark(index); this.syncThumbnail(true); }
      };
      const settleNavigation = () => {
        if (this.navigation === null) return;
        this.navigation = null;
        clearTimeout(this.scrollEnd);
        this.mark(this.nearestIndex());
        this.syncThumbnail(true);
      };
      this.track.addEventListener('pointerdown', interruptNavigation, opts);
      this.track.addEventListener('wheel', interruptNavigation, {...opts, passive:true});
      this.track.addEventListener('scrollend', settleNavigation, opts);
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
          // Explicit clicks keep their requested target during a smooth jump.
          // A real gesture cancels that target and follows each visible image
          // immediately, even when successive swipes never pause.
          if (this.navigation !== null) {
            if (Math.abs(this.track.scrollLeft - this.slides[this.navigation].offsetLeft) < 2) {
              this.navigation = null;
              clearTimeout(this.scrollEnd);
            } else {
              clearTimeout(this.scrollEnd);
              this.scrollEnd = setTimeout(settleNavigation, 120);
              return;
            }
          }
          const index = this.nearestIndex();
          if (index !== this.index) { this.mark(index); this.syncThumbnail(true); }
        });
      }, {...opts, passive:true});
      this.rail = this.thumbs[0]?.parentElement;
      if (this.rail) {
        this.rail.addEventListener('dragstart', e => e.preventDefault(), opts);
        this.rail.addEventListener('pointerdown', e => {
          if (e.pointerType !== 'mouse' || e.button !== 0) return;
          this.drag = {id:e.pointerId, x:e.clientX, y:e.clientY, left:this.rail.scrollLeft, top:this.rail.scrollTop, vertical:matchMedia('(min-width:750px)').matches, active:false};
        }, opts);
        window.addEventListener('pointermove', e => {
          const drag = this.drag;
          if (!drag || e.pointerId !== drag.id) return;
          const delta = drag.vertical ? e.clientY - drag.y : e.clientX - drag.x;
          if (!drag.active && Math.abs(delta) < 6) return;
          if (!drag.active) {
            drag.active = true;
            this.rail.setPointerCapture(e.pointerId);
            this.rail.dataset.rhamaDragging = 'true';
          }
          e.preventDefault();
          this.rail.scrollTo({left:drag.vertical ? drag.left : drag.left - delta, top:drag.vertical ? drag.top - delta : drag.top, behavior:'instant'});
        }, {...opts, passive:false});
        const endDrag = e => {
          if (!this.drag || e.pointerId !== this.drag.id) return;
          this.suppressClick = this.drag.active && e.type === 'pointerup';
          if (this.rail.hasPointerCapture(e.pointerId)) this.rail.releasePointerCapture(e.pointerId);
          delete this.rail.dataset.rhamaDragging;
          this.drag = null;
        };
        window.addEventListener('pointerup', endDrag, opts);
        window.addEventListener('pointercancel', endDrag, opts);
      }
      this.mark(0);
      if (root.rhamaPendingMedia) this.goToMedia(...root.rhamaPendingMedia);
      root.dataset.rhamaReady = 'true';
    }
    go(index, smooth = true) {
      // The existing gallery is configured to loop. Keep arrow/keyboard wrapping.
      index = (index + this.slides.length) % this.slides.length;
      clearTimeout(this.scrollEnd);
      this.navigation = index;
      this.mark(index);
      this.track.scrollTo({left: this.slides[index].offsetLeft, behavior: smooth && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant'});
      if (Math.abs(this.track.scrollLeft - this.slides[index].offsetLeft) < 2) this.navigation = null;
      this.syncThumbnail(true);
    }
    nearestIndex() {
      let index = 0, distance = Infinity;
      this.slides.forEach((slide, i) => {
        const delta = Math.abs(slide.offsetLeft - this.track.scrollLeft);
        if (delta < distance) { index = i; distance = delta; }
      });
      return index;
    }
    syncThumbnail(center = false) {
      if (this.drag?.active) return;
      const thumb = this.thumbs[this.index], rail = thumb?.parentElement;
      if (thumb && rail) {
        const item = thumb.getBoundingClientRect(), bounds = rail.getBoundingClientRect();
        if (!center && item.left >= bounds.left - 1 && item.right <= bounds.right + 1 && item.top >= bounds.top - 1 && item.bottom <= bounds.bottom + 1) return;
        const vertical = matchMedia('(min-width:750px)').matches;
        rail.scrollTo({left: vertical ? 0 : thumb.offsetLeft - (rail.clientWidth - thumb.clientWidth) / 2, top: vertical ? thumb.offsetTop - (rail.clientHeight - thumb.clientHeight) / 2 : 0, behavior:'instant'});
      }
    }
    goToMedia(mediaId, smooth = false) {
      const id = String(mediaId).split('-').pop();
      const index = this.slides.findIndex(s => s.dataset.mediaId === id || s.querySelector(`[data-media-id="${id}"]`));
      if (index < 0) return false;
      this.go(index, smooth); return true;
    }
    mark(index) {
      this.index = Math.max(0, Math.min(this.slides.length - 1, index));
      this.thumbs.forEach((t,i) => {t.classList.toggle('active',i===this.index);t.setAttribute('aria-current',String(i===this.index));});
      const counter = this.root.querySelector('.swiper-pagination-fraction');
      if (counter) counter.textContent = `${this.index + 1} / ${this.slides.length}`;
    }
    destroy() {
      this.abort.abort(); cancelAnimationFrame(this.frame); clearTimeout(this.scrollEnd);
      if (this.drag && this.rail?.hasPointerCapture(this.drag.id)) this.rail.releasePointerCapture(this.drag.id);
      if (this.rail) delete this.rail.dataset.rhamaDragging;
      this.drag = null;
    }
  }
  window.RhamaNativeGallery = RhamaNativeGallery;
  document.dispatchEvent(new Event('rhama:native-gallery-ready'));
})();
